import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, List, ListItem, ListItemText,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Close as RevokeIcon,
  PersonAdd as InviteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { invitationService } from '../../services/invitationService';
import { track } from '../../utils/analytics';

const STATUS_COLOUR = {
  PENDING: 'info',
  ACCEPTED: 'success',
  REVOKED: 'default',
  EXPIRED: 'default',
};

/**
 * Inviting somebody into this organisation, and what has been sent so far.
 *
 * <p>The invitation is a link, not an email FlowGate sends: there is no mail provider configured
 * on this deployment, and a link the inviter sends themselves arrives from somebody the invitee
 * recognises. The link comes back once, on the response that created it, so it is shown once,
 * with a copy button, and the row afterwards can only say that an invitation exists.
 */
const InvitationsPanel = ({ roles, canInvite }) => {
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { selectedOrganization } = useOrganization();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [roleName, setRoleName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [freshLink, setFreshLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const organisationId = selectedOrganization?.id;
  const roleNames = (roles || []).map((role) => role.name);

  useEffect(() => {
    if (!roleName && roleNames.length) {
      setRoleName(roleNames.includes('Editor') ? 'Editor' : roleNames[0]);
    }
  }, [roleNames, roleName]);

  const load = useCallback(async () => {
    if (!organisationId) return;
    setLoading(true);
    try {
      setInvitations(await invitationService.list(organisationId, authenticatedFetch));
    } catch {
      // A panel that cannot list invitations can still send one; no error banner for a read.
      setInvitations([]);
    } finally {
      setLoading(false);
    }
    // authenticatedFetch is recreated every AuthContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  useEffect(() => { load(); }, [load]);

  const send = async (event) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    setFreshLink(null);
    setCopied(false);
    try {
      const created = await invitationService.invite(
        organisationId, { email: email.trim(), roleName }, authenticatedFetch,
      );
      setFreshLink(invitationService.linkFor(created.token));
      setEmail('');
      track('member_invited', { roleName });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(freshLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const revoke = async (invitation) => {
    setError(null);
    try {
      await invitationService.revoke(organisationId, invitation.id, authenticatedFetch);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const pendingFirst = [...invitations].sort((a, b) => {
    if (a.status === b.status) return 0;
    return a.status === 'PENDING' ? -1 : 1;
  });

  return (
    <Box sx={{ mt: 4 }} data-testid="invitations-panel">
      <Typography variant="h6" gutterBottom>Invitations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Invite a colleague and send them the link. They sign in with the address you invite, and
        arrive with the role you choose.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {canInvite && !isDemoMode && (
        <Box component="form" onSubmit={send} sx={{ mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <TextField
              size="small"
              type="email"
              label="Email address"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 220 }}
              inputProps={{ 'aria-label': 'Email address to invite' }}
            />
            <TextField
              select
              size="small"
              label="Role"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              sx={{ minWidth: 150 }}
              SelectProps={{ native: false }}
              inputProps={{ 'aria-label': 'Role' }}
            >
              {roleNames.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} /> : <InviteIcon />}
              disabled={sending || !email.trim() || !roleName}
              sx={{ flexShrink: 0 }}
            >
              Create invite
            </Button>
          </Stack>
        </Box>
      )}

      {freshLink && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          data-testid="fresh-invite-link"
          action={(
            <Button size="small" startIcon={<CopyIcon />} onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          )}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Send them this link. It is shown once.
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{freshLink}</Typography>
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={22} />
      ) : pendingFirst.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody has been invited yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {pendingFirst.map((invitation) => (
            <ListItem
              key={invitation.id}
              divider
              secondaryAction={canInvite && invitation.status === 'PENDING' ? (
                <Tooltip title="Withdraw this invitation">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => revoke(invitation)}
                    aria-label={`Withdraw the invitation to ${invitation.email}`}
                  >
                    <RevokeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            >
              <ListItemText
                primary={invitation.email}
                secondary={invitation.roleName}
                primaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
              />
              <Chip
                size="small"
                label={invitation.status.toLowerCase()}
                color={STATUS_COLOUR[invitation.status] || 'default'}
                sx={{ mr: canInvite && invitation.status === 'PENDING' ? 4 : 0 }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default InvitationsPanel;
